import React, { useEffect } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { Box, Grid, Tooltip, Typography } from '@mui/material';
import CheckBoxOutlinedIcon from '@mui/icons-material/CheckBoxOutlined';
import { CancelOutlined, CheckCircleOutline } from '@mui/icons-material';
import ArrowBackIosRoundedIcon from '@mui/icons-material/ArrowBackIosRounded';
import DeleteOutlinedIcon from '@mui/icons-material/DeleteOutlined';
import { fetchCompanyDetails, deleteCompany } from 'redux/actions/companyAction';
import { abbreviateNumber, getDateFormat } from 'utils/utils';
import { billingTypeEnum } from 'utils/enumObj';
import { roleAndPermissionEnum } from 'utils/rolesPermissionsEnum';
import useModal from 'hooks/useModal';
import CommonButton from 'components/commonButton/CommonButton';
import Loader from 'components/loader/Loader';
import ConfirmModal from 'components/confirmModal/ConfirmModal';
import CommonDialog from 'components/commonDialog/CommonDialog';
import DownloadIcon from 'assets/images/download.png';
import car from 'assets/images/companyDetailsCar.svg';
import revenue from 'assets/images/companyDetailsRevenue.svg';
import emailImg from 'assets/images/EnvelopeSimple.svg';
import RateCardV1 from './rateCardNew/rateCardNew';
import GarageRateDisplay from './CompanyGarageSlabRateConfig';

const CompanyProfile = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { state } = useLocation();
  const { id } = useParams();
  const backData = state?.payload;
  const { open, toggle } = useModal();
  const { open: deleteConfirmOpen, toggle: toggleDeleteConfirm } = useModal();

  let filterData = {
    companyId: id,
  };

  useEffect(() => {
    dispatch(fetchCompanyDetails({ id: id }));
  }, []);

  const companyProfile = useSelector(
    (state) => state?.userCompanyDetails?.companyDetails
  );

  const companyTrip = useSelector(
    (state) => state?.userTopCompanies?.topCompanies?.data?.[0]
  );

  const ContractDetails = useSelector(
    (state) =>
      state?.userCompanyDetails?.companyDetails?.Contracts?.[0]?.ContractDetails
  );
  const headOffice = companyProfile?.branch?.filter(
    (item) => item.isHeadquarter === true
  )[0];
  const branches = companyProfile?.branch?.filter(
    (item) => item.isHeadquarter !== true
  );

  const allowedDomain = companyProfile?.allowedDomain;

  const vehicleData = ContractDetails?.filter((car) => car.type === 'vehicle');
  let vehicleName = vehicleData?.map((ele) => ele?.vehicle);

  const dutyData = ContractDetails?.filter((duty) => duty.type === 'dutyType');
  let dutyName = dutyData?.map((ele) => ele?.dutyType);

  const loaderCompnayProfile = useSelector(
    (state) => state?.userCompanyDetails?.loading
  );

  const combineDates = (startDate, endDate) => {
    if (startDate && endDate) {
      const formattedStartDate = getDateFormat(startDate, 'moreDetails');
      const formattedEndDate = getDateFormat(endDate, 'moreDetails');
      return `${formattedStartDate} - ${formattedEndDate}`;
    }

    return 'NA';
  };

  const formatGSTNumber = (gstNumber, sez) => {
    if (gstNumber) {
      // Check if gstNumber is a string before converting to uppercase
      const formattedGSTNumber =
        typeof gstNumber === 'string' ? gstNumber.toUpperCase() : gstNumber;

      if (sez === true) {
        return `${formattedGSTNumber} (SEZ)`;
      } else {
        return formattedGSTNumber;
      }
    }

    return 'NA';
  };

  const onClickMoreIcon = () => {
    toggle();
  };

  const handleDeleteIconClick = () => {
    toggleDeleteConfirm();
  };

  const handleConfirmDelete = () => {
    toggleDeleteConfirm();
    dispatch(deleteCompany(id, navigate));
  };

  const handleVisitClientPortal = () => {
    if (companyProfile?.clientUrl) {
      const token = localStorage.getItem('token');
      const loginUrl = `${companyProfile.clientUrl}/login?Token=${token}`;
      window.open(loginUrl, '_blank');
    }
  };

  const financeDetails = companyProfile?.Employees?.filter(
    (item, index) => item?.userRole === 'Finance'
  );
  const adminDetails = companyProfile?.Employees?.filter(
    (item, index) => item?.userRole === 'Admin'
  );

  const details = {
    companyDetails: {
      // 'Parent Company': companyProfile?.parentCompanyName || 'NA',
      Address: companyProfile?.address || 'NA',
      City: companyProfile?.cityName || 'NA',
      'Pin Code': companyProfile?.pinCode || 'NA',
      'Company PAN': (companyProfile?.panNumber || 'NA').toUpperCase(),
      'Email ID': companyProfile?.email || 'NA',
      'Phone Number': companyProfile?.phoneNumber || 'NA',
      // 'Secure Trip End with OTP': companyProfile?.additionalInfo?.endOtpRequired ? <CheckCircleOutline /> : <CancelOutlined />
    },
    gstDetails: {
      'GST Type': companyProfile?.BillingDetail?.gstType || 'NA',
      'GST No.': companyProfile?.BillingDetail?.gstNumber || 'NA',
      'Billing Name': companyProfile?.BillingDetail?.name || 'NA',
      'Billing Address': companyProfile?.BillingDetail?.address || 'NA',
      State: companyProfile?.BillingDetail?.state || 'NA',
    },
    contractDetails: {
      'Contract Date':
        getDateFormat(companyProfile?.Contracts?.[0]?.date) || 'NA',
      Tenure:
        combineDates(
          companyProfile?.Contracts?.[0]?.startDate,
          companyProfile?.Contracts?.[0]?.endDate
        ) || 'NA',
    },
    headOffice: {
      City: headOffice?.cityName || 'NA',
      Address: headOffice?.address || 'NA',
      State: headOffice?.state || 'NA',
      'GST No': formatGSTNumber(headOffice?.gstNumber, headOffice?.sez) || 'NA',
      'Garage Location': headOffice?.additionalInfo?.garageAddress || ''
    },
    billingDetails: {
      'Billing Cycle': companyProfile?.Contracts?.[0]?.billingCycle || 'NA',
      'Billing Type': companyProfile?.Contracts?.[0]?.invoiceType || 'NA',
      'Duty Configuration':
        companyProfile?.Contracts?.[0]?.config === 'Point-To-Garage'
          ? ' Point to Point with Fixed Garage Run'
          : companyProfile?.Contracts?.[0]?.config === 'Garage-To-Garage'
          ? ' Garage to Garage'
          : companyProfile?.Contracts?.[0]?.config === 'Point-To-Point'
          ? 'Point-To-Point'
          : 'NA',
      'Approve or Dispute Duration(in hr)':
        companyProfile?.Contracts?.[0]?.approvalDuration,
    },
    billingDetailsFixedGarage: {
      'Billing Cycle': companyProfile?.Contracts?.[0]?.billingCycle || 'NA',
      'Billing Type': companyProfile?.Contracts?.[0]?.invoiceType || 'NA',
      'Duty Configuration':
        companyProfile?.Contracts?.[0]?.config === 'Point-To-Garage'
          ? ' Point to Point with Fixed Garage Run'
          : companyProfile?.Contracts?.[0]?.config === 'Garage-To-Garage'
          ? ' Garage to Garage'
          : companyProfile?.Contracts?.[0]?.config === 'Point-To-Point'
          ? 'Point-To-Point'
          : 'NA',
      'Fixed Garage Run':
        '₹ ' + companyProfile?.Contracts?.[0]?.configRate || '0',
      'Approve or Dispute Duration(in hr)':
        companyProfile?.Contracts?.[0]?.approvalDuration,
    },
    financeDetails: {
      Name: financeDetails?.[0]?.name || 'NA',
      'Email ID': financeDetails?.[0]?.email || 'NA',
      'Phone No': financeDetails?.[0]?.phoneNumber || 'NA',
    },
    adminDetails: {
      Name: adminDetails?.[0]?.name || 'NA',
      'Email ID': adminDetails?.[0]?.email || 'NA',
      'Phone No': adminDetails?.[0]?.phoneNumber || 'NA',
    },
  };

  const companyRoleEnum = roleAndPermissionEnum.company;
  // const roleAndPermissions = useSelector(
  //   (state) => state?.getRolesAndPermissions?.rolesAndPermissions
  // );
  // const accessForEdit = handlePermissions(
  //   // roleAndPermissions,
  //   companyRoleEnum.moduleKey,
  //   {
  //     defaultAllAccess: companyRoleEnum.all,
  //     customAccess: companyRoleEnum.edit,
  //   }
  // );

  return (
    <Grid container justifyContent={'center'}>
      {loaderCompnayProfile ? (
        <Loader />
      ) : (
        <Grid item xs={12} lg={11} className="bgWhite p30">
          <Box className="justifyBetween">
            <Box className="alignStart">
              <ArrowBackIosRoundedIcon
                className="font24 colorGrey"
                role="presentation"
                onClick={() =>
                  navigate(`/companies`, {
                    state: { payload: backData, back: true },
                  })
                }
              />
            </Box>
            <Box>
              <Tooltip title="Delete Company">
                <DeleteOutlinedIcon
                  className="font24 colorGrey cursor"
                  onClick={handleDeleteIconClick}
                />
              </Tooltip>
            </Box>
            {/* {accessForEdit && (
              <CommonButton
                name={'Edit'}
                iconType="edit"
                btnClass={'btnSm btnEdit'}
                iconPlace="front"
                onClick={() =>
                  navigate('/companies/new-booking', {
                    state: { edit: true, companyProfile: companyProfile },
                  })
                }
              />
            )} */}
          </Box>

          <Box className="bgYellow mt30 alignStart br5 mb30">
            <Typography variant="head5" className="p20 fw600">
              {companyProfile?.name}
            </Typography>
          </Box>
          {/* <Grid container className="justifyBetween mb40">
            <Grid
              container
              item
              md={5.8}
              sm={12}
              className="justifyCenter borderForm height150 backGroundLightGrey"
            >
              <Grid item>
                <img src={car} alt="car" />
              </Grid>
              <Grid item className="ml40">
                <Typography variant="head2" className="fw600">
                  {companyTrip?.count || 0}
                </Typography>
                <Typography variant="body1" className="colorGrey">
                  Trips
                </Typography>
              </Grid>
            </Grid>
            <Grid
              container
              item
              md={5.8}
              sm={12}
              className="justifyCenter borderForm height150 backGroundLightGrey"
            >
              <Grid item>
                <img src={revenue} alt="car" />
              </Grid>
              <Grid item className="ml40">
                <Tooltip
                  title={'₹' + (companyTrip?.amount || 0)}
                  placement="top"
                >
                  <Typography variant="head2" className="fw600">
                    {'₹' + abbreviateNumber(companyTrip?.amount || 0)}
                  </Typography>
                </Tooltip>
                <Typography variant="body1" className="colorGrey">
                  transactions so far...
                </Typography>
              </Grid>
            </Grid>
          </Grid> */}

          <Grid container className="mb30">
            {Object.entries(details?.companyDetails).map((objData, index) => (
              <Grid
                container
                item
                xs={12}
                lg={
                  objData[0] === 'Address' || objData[0] === 'Parent Company'
                    ? 12
                    : 6
                }
                key={index}
              >
                <Grid
                  item
                  xs={
                    objData[0] === 'Address' || objData[0] === 'Parent Company'
                      ? 2
                      : 4
                  }
                  className="gridBorder"
                >
                  <Typography variant={'body2'} className="colorGrey fw400 p20">
                    {objData[0]}
                  </Typography>
                </Grid>
                <Grid
                  item
                  xs={
                    objData[0] === 'Address' || objData[0] === 'Parent Company'
                      ? 10
                      : 8
                  }
                  className="gridBorder"
                >
                  <Typography
                    variant={'body2'}
                    className="colorBlack fw400 p20"
                  >
                    {objData[1]}
                  </Typography>
                </Grid>
              </Grid>
            ))}
          </Grid>

          {allowedDomain?.length > 0 && (
            <Grid container className="mb30">
              <Grid item xs={12} lg={12} className="gridBorder p20">
                <Typography variant="head5" className="fw600">
                  Email Provider Details
                </Typography>
              </Grid>
              <Grid
                container
                direction="row"
                justifyContent="flex-start"
                alignItems="center"
              >
                {allowedDomain?.map((objData, index) => (
                  <Grid
                    item
                    xs={12}
                    md={4}
                    className="gridBorder alignItem p20"
                  >
                    <img src={emailImg} alt="email" className="mr6" />
                    <Typography variant={'body2'}>{objData}</Typography>
                  </Grid>
                ))}
              </Grid>
            </Grid>
          )}

          {companyProfile?.clientUrl && (
            <Box sx={{ mb: 3 }}>
              <CommonButton
                name={'Access Admin Portal'}
                iconType="openInNew"
                btnClass={'btnYellow btnPortal'}
                onClick={handleVisitClientPortal}
              />
            </Box>
          )}

          {/* <Box className="bgGrey alignStart br5 mb30">
            <Typography variant="head5" className="p20 fw600">
              Contract Details
            </Typography>
          </Box>
          <Grid container className="mb20">
            {Object.entries(details?.contractDetails).map((objData, index) => (
              <Grid container item xs={12} lg={5.5} key={index}>
                <Grid item xs={4} className="gridBorder">
                  <Typography variant={'body2'} className="colorGrey fw400 p20">
                    {objData[0]}
                  </Typography>
                </Grid>
                <Grid item xs={8} className="gridBorder">
                  <Typography
                    variant={'body2'}
                    className="colorBlack fw400 p20"
                  >
                    {objData[1]}
                  </Typography>
                </Grid>
              </Grid>
            ))}
            <Grid item xs={12} lg={1} className="justifyCenter gridBorder">
              <img
                src={DownloadIcon}
                alt="ArrowDown"
                className="cursor"
                onClick={onClickMoreIcon}
              />
            </Grid>
          </Grid> */}

          {/* ---Office Details */}
          {/* <Box className="bgGrey alignStart br5 mb30">
            <Typography variant="head5" className="p20 fw600">
              Office Details
            </Typography>
          </Box>
          {headOffice && (
            <Typography variant="body2" className="colorPrimary fw600">
              Head Office Details
            </Typography>
          )}
          <Grid container className="mb20">
            {Object.entries(details?.headOffice).map((objData, index) => (
              <Grid container item xs={12} lg={6} key={index}>
                <Grid item xs={4} className="gridBorder">
                  <Typography variant={'body2'} className="colorGrey fw400 p20">
                    {objData[0]}
                  </Typography>
                </Grid>
                <Grid item xs={8} className="gridBorder">
                  <Typography
                    variant={'body2'}
                    className="colorBlack fw400 p20"
                  >
                    {objData[1]}
                  </Typography>
                </Grid>
              </Grid>
            ))}
          </Grid> */}

          {/* ---Branches--- */}

          <Grid container className="mb20">
            {branches?.map((branch, index) => (
              <>
                <Grid item lg={12}>
                  <Typography
                    variant="body2"
                    className="colorPrimary font16 fw600 pt20"
                  >
                    Branch {index + 1} Office Details
                  </Typography>
                </Grid>
                <Grid container item xs={12} lg={6} key={index}>
                  <Grid item xs={4} className="gridBorder">
                    <Typography
                      variant={'body2'}
                      className="colorGrey fw400 p20"
                    >
                      City
                    </Typography>
                  </Grid>
                  <Grid item xs={8} className="gridBorder">
                    <Typography
                      variant={'body2'}
                      className="colorBlack fw400 p20"
                    >
                      {branch?.cityName || 'NA'}
                    </Typography>
                  </Grid>
                </Grid>

                <Grid container item xs={12} lg={6} key={index}>
                  <Grid item xs={4} className="gridBorder">
                    <Typography
                      variant={'body2'}
                      className="colorGrey fw400 p20"
                    >
                      Address
                    </Typography>
                  </Grid>
                  <Grid item xs={8} className="gridBorder">
                    <Typography
                      variant={'body2'}
                      className="colorBlack fw400 p20"
                    >
                      {branch?.address ? branch?.address : 'NA'}
                    </Typography>
                  </Grid>
                </Grid>

                <Grid container item xs={12} lg={6} key={index}>
                  <Grid item xs={4} className="gridBorder">
                    <Typography
                      variant={'body2'}
                      className="colorGrey fw400 p20"
                    >
                      State
                    </Typography>
                  </Grid>
                  <Grid item xs={8} className="gridBorder">
                    <Typography
                      variant={'body2'}
                      className="colorBlack fw400 p20"
                    >
                      {branch?.state ? branch?.state : 'NA'}
                    </Typography>
                  </Grid>
                </Grid>

                <Grid container item xs={12} lg={6} key={index}>
                  <Grid item xs={4} className="gridBorder">
                    <Typography
                      variant={'body2'}
                      className="colorGrey fw400 p20"
                    >
                      GST
                    </Typography>
                  </Grid>
                  <Grid item xs={8} className="gridBorder">
                    <Typography
                      variant={'body2'}
                      className="colorBlack fw400 p20"
                    >
                      {branch?.gstNumber
                        ? branch?.gstNumber.toUpperCase()
                        : 'NA'}
                      {branch?.sez === true ? '(SEZ)' : null}
                    </Typography>
                  </Grid>
                </Grid>
                <Grid container item xs={12} lg={6} key={index}>
                  <Grid item xs={4} className="gridBorder">
                    <Typography
                      variant={'body2'}
                      className="colorGrey fw400 p20"
                    >
                      Garage Location
                    </Typography>
                  </Grid>
                  <Grid item xs={8} className="gridBorder">
                    <Typography
                      variant={'body2'}
                      className="colorBlack fw400 p20"
                    >
                      {branch?.additionalInfo?.garageAddress
                        ? branch?.additionalInfo?.garageAddress
                        : 'NA'}
                    </Typography>
                  </Grid>
                </Grid>
              </>
            ))}
          </Grid>

          {/* ---SEZ--- */}
          <Grid container>
            {companyProfile?.sez ? (
              <Grid item xs={12} lg={4} className="alignStart">
                <Typography
                  variant={'body2'}
                  className="colorBlack fw400  flexAlignCenter"
                >
                  <span className="colorPrimary">
                    <CheckBoxOutlinedIcon />
                  </span>
                  <span className="ml5">SEZ(Special Economic Zone)</span>
                </Typography>
              </Grid>
            ) : null}
            {/* ---Image--- */}
          </Grid>

          {/* ---Billing */}
          {/* <Box className="bgGrey alignStart br5 mb30">
            <Typography variant="head5" className="p20 fw600">
              Billing Details
            </Typography>
          </Box>
          {companyProfile?.Contracts?.[0]?.config === 'Garage-To-Garage' 
            ? (<Grid container className="mb20">
                {Object.entries(details?.billingDetails
                ).map((objData, index) => (
                  <Grid container item xs={12} lg={6} key={index}>
                    <Grid item xs={4} className="gridBorder">
                      <Typography variant={'body2'} className="colorGrey fw400 p20">
                        {objData[0]}
                      </Typography>
                    </Grid>
                    <Grid item xs={8} className="gridBorder">
                      <Typography variant={'body2'} className="colorBlack fw400 p20">
                        {objData[1]}
                      </Typography>
                    </Grid>
                  </Grid>))}
                <GarageRateDisplay additionalInfo={companyProfile?.additionalInfo} />
              </Grid>
            )
            : (
              <Grid container className="mb20">
                {Object.entries(
                  companyProfile?.Contracts?.[0]?.config === 'Point-To-Garage'
                    ? details?.billingDetailsFixedGarage
                    : details?.billingDetails
                ).map((objData, index) => (
                  <Grid container item xs={12} lg={6} key={index}>
                    <Grid item xs={4} className="gridBorder">
                      <Typography variant={'body2'} className="colorGrey fw400 p20">
                        {objData[0]}
                      </Typography>
                    </Grid>
                    <Grid item xs={8} className="gridBorder">
                      <Typography variant={'body2'} className="colorBlack fw400 p20">
                        {objData[1]}
                      </Typography>
                    </Grid>
                  </Grid>
                ))}
              </Grid>
            )
          } */}

          {/* <Box className="mb20">
            {companyProfile?.Contracts?.[0]?.billingType ===
            billingTypeEnum?.billToCreditCardAndbillToCompany ? (
              <Grid item xs={12} lg={6} className="alignStart">
                <Typography
                  variant={"body2"}
                  className="colorBlack fw400  flexAlignCenter"
                >
                  <span className="colorPrimary">
                    <CheckBoxOutlinedIcon />
                  </span>
                  <span className="ml5">Bill To Credit Card</span>
                </Typography>
                <Typography
                  variant={'body2'}
                  className="colorBlack fw400  flexAlignCenter ml40"
                >
                  <span className="colorPrimary">
                    <CheckBoxOutlinedIcon />
                  </span>
                  <span className="ml5">Bill To Company</span>
                </Typography>
              </Grid>
            ) : companyProfile?.Contracts?.[0]?.billingType ===
              billingTypeEnum?.billToCompany ? (
              <Grid item xs={12} lg={4} className="alignStart">
                <Typography
                  variant={'body2'}
                  className="colorBlack fw400  flexAlignCenter"
                >
                  <span className="colorPrimary">
                    <CheckBoxOutlinedIcon />
                  </span>
                  <span className="ml5">Bill To Company</span>
                </Typography>
              </Grid>
            )  : companyProfile?.Contracts?.[0]?.billingType ===
              billingTypeEnum?.billToCreditCard ? (
              <Grid item xs={12} lg={4} className="alignStart">
                <Typography
                  variant={"body2"}
                  className="colorBlack fw400  flexAlignCenter"
                >
                  <span className="colorPrimary">
                    <CheckBoxOutlinedIcon />
                  </span>
                  <span className="ml5">Bill To Credit Card</span>
                </Typography>
              </Grid>
            ) :
            null}
          </Box> */}

          {/* ---Admin Details--- */}
          {/* <Box className="bgGrey alignStart br5 mb30">
            <Typography variant="head5" className="p20 fw600">
              Admin Details
            </Typography>
          </Box>
          <Grid container className="mb30">
            {Object.entries(details.adminDetails).map((objData, index) => (
              <Grid container item xs={12} lg={6} key={index}>
                <Grid item xs={4} className="gridBorder">
                  <Typography variant={'body2'} className="colorGrey fw400 p20">
                    {objData[0]}
                  </Typography>
                </Grid>
                <Grid item xs={8} className="gridBorder">
                  <Typography
                    variant={'body2'}
                    className="colorBlack fw400 p20"
                  >
                    {objData[1]}
                  </Typography>
                </Grid>
              </Grid>
            ))}
          </Grid> */}

          {/* ---Finance Details--- */}
          {/* <Box className="bgGrey alignStart br5 mb30">
            <Typography variant="head5" className="p20 fw600">
              Finance Details
            </Typography>
          </Box>
          <Grid container className="mb30">
            {Object.entries(details.financeDetails).map((objData, index) => (
              <Grid container item xs={12} lg={6} key={index}>
                <Grid item xs={4} className="gridBorder">
                  <Typography variant={'body2'} className="colorGrey fw400 p20">
                    {objData[0]}
                  </Typography>
                </Grid>
                <Grid item xs={8} className="gridBorder">
                  <Typography
                    variant={'body2'}
                    className="colorBlack fw400 p20"
                  >
                    {objData[1]}
                  </Typography>
                </Grid>
              </Grid>
            ))}
          </Grid> */}
          {/* <Box className="bgGrey alignStart br5 mb10">
            <Typography variant="head5" className="p20 fw600">
              Rate Card
            </Typography>
          </Box>
          <RateCardV1></RateCardV1> */}
        </Grid>
      )}
      <ConfirmModal
        open={open}
        toggle={toggle}
        pageName={'companyDetails'}
        companyProfile={companyProfile}
        onDelete={handleConfirmDelete}
      />
      
      <CommonDialog
        open={deleteConfirmOpen}
        handleClose={toggleDeleteConfirm}
        selectType="delete"
        typeOfDialog="Company"
        text="Are you sure you want to delete this company? This action cannot be undone."
        onConfirmDialog={handleConfirmDelete}
      />
    </Grid>
  );
};

export default CompanyProfile;
